import React from 'react';
import { AlertTriangle, Calendar, ChevronDown, ChevronRight, Clock, Info, Lock, Unlock } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

// Repli si le serveur n'envoie pas encore le détail (version antérieure).
const ETAPES_REPLI = [
  { cle: 'avances', libelle: 'Avances', bloquante: true },
  { cle: 'etat_salaires', libelle: 'État des salaires', bloquante: false },
  { cle: 'demande_dotation', libelle: 'Demande de dotation', bloquante: true },
  { cle: 'validation_centrale', libelle: 'Validation centrale', bloquante: false },
  { cle: 'paiement_salaires', libelle: 'Paiement', bloquante: true }
];

// Calendrier du mois de paie affiché : les dates RÉELLES de chaque étape
// (week-ends reportés au jour ouvré suivant, utils/fenetresPaie.js), et ce
// que le système bloque hors de ces dates. Le serveur applique exactement ces
// dates : c'est lui qui les calcule.
// `peutOuvrir` : la personne peut demander (et refermer) une ouverture
// exceptionnelle ; le bouton n'apparaît que pendant le mois de paie en cours,
// sur une étape bloquante, quand rien n'est déjà ouvert ni demandé.
const CalendrierPaieSection = ({
  isExpanded,
  onToggleSection,
  calendrier = {},
  detail = null,
  peutOuvrir = false,
  onDemanderOuverture,
  onRefermerOuverture
}) => {
  const etapes = detail?.etapes || ETAPES_REPLI.map((e) => ({ ...e, dates: calendrier[e.cle] || '-', decalages: [] }));
  const estCourant = Boolean(detail?.est_courant);

  return (
    <div className="px-6">
      <Card>
        <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors" onClick={onToggleSection}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isExpanded ? (
                <ChevronDown size={24} className="text-gray-600" />
              ) : (
                <ChevronRight size={24} className="text-gray-600" />
              )}
              <div>
                <CardTitle className="text-xl text-[#246990] flex items-center gap-2">
                  <Calendar size={24} />
                  Calendrier mensuel de paie
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Dates réelles du mois, calculées à partir des paramètres salaires
                </p>
              </div>
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="pt-2 space-y-4">
            {detail?.message && (
              <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${estCourant
                ? 'border-amber-200 bg-amber-50 text-amber-900'
                : 'border-[#246990]/20 bg-[#eef7fb] text-[#246990]'}`}
              >
                {estCourant ? <Lock size={16} className="mt-0.5 shrink-0" /> : <Info size={16} className="mt-0.5 shrink-0" />}
                <p className="leading-relaxed">{detail.message}</p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-5">
              {etapes.map((etape, index) => {
                return (
                  <div key={etape.cle} className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 p-3">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#246990] text-xs font-semibold text-white">
                        {index + 1}
                      </span>
                      <h3 className="truncate text-sm font-semibold text-gray-900">{etape.libelle}</h3>
                    </div>
                    <div className="flex flex-1 flex-col gap-2 p-3">
                      <span className={`inline-flex h-6 w-[92px] items-center justify-center rounded-full border text-xs font-medium ${etape.bloquante
                        ? 'border-red-200 bg-red-50 text-red-700'
                        : 'border-gray-200 bg-gray-50 text-gray-600'}`}
                      >
                        {etape.bloquante ? 'Bloquant' : 'Indicatif'}
                      </span>
                      <p className="text-sm font-semibold text-gray-900 first-letter:uppercase">{etape.dates}</p>
                      {etape.reglage && <p className="text-[11px] text-gray-500">Réglage : {etape.reglage}</p>}
                      {etape.decalages?.map((texte) => (
                        <p key={texte} className="flex items-start gap-1.5 text-[11px] text-amber-800">
                          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                          <span>{texte}</span>
                        </p>
                      ))}
                      {etape.regle && <p className="text-xs text-gray-600">{etape.regle}</p>}

                      {etape.ouverture && (
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-900">
                          <p className="flex items-start gap-1.5 font-semibold">
                            <Unlock size={12} className="mt-0.5 shrink-0" />
                            <span>Ouverture exceptionnelle jusqu'au {etape.ouverture.jusqu_au}</span>
                          </p>
                          {etape.ouverture.accordee_par && (
                            <p className="mt-0.5">
                              Accordée par {etape.ouverture.accordee_par}{etape.ouverture.auto_validee ? ' (auto-validée)' : ''}
                            </p>
                          )}
                          {peutOuvrir && (
                            <button
                              type="button"
                              onClick={() => onRefermerOuverture?.(etape)}
                              className="mt-2 inline-flex h-7 items-center gap-1.5 rounded-md border border-emerald-300 bg-white px-2 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100"
                            >
                              <Lock size={12} />
                              Refermer
                            </button>
                          )}
                        </div>
                      )}

                      {!etape.ouverture && etape.demande_en_attente && (
                        <p className="flex items-start gap-1.5 rounded-md border border-gray-200 bg-gray-50 p-2 text-[11px] text-gray-700">
                          <Clock size={12} className="mt-0.5 shrink-0" />
                          <span>Demande d'ouverture n° {etape.demande_en_attente} en attente au Centre de Support</span>
                        </p>
                      )}

                      {peutOuvrir && estCourant && etape.bloquante && !etape.ouverture && !etape.demande_en_attente && etape.date_debut && (
                        <button
                          type="button"
                          onClick={() => onDemanderOuverture?.(etape)}
                          className="mt-auto inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
                        >
                          <Unlock size={13} />
                          Demander une ouverture exceptionnelle
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default CalendrierPaieSection;
